// frontend/src/components/flags/flagPaths.ts
/**
 * Write `value` at dot `path` into a copy of the tenant's whole `feature_flags`
 * object, leaving every other key untouched.
 *
 * The path is resolved from the ROOT of `feature_flags`, exactly as GUS
 * resolves it (`getValueByPath(config.features, path)` in ConfigContext, where
 * `config.features` IS `feature_flags`). It is not rooted at `modules`: nine of
 * the real manifest's eighty nodes -- `utilities.settings` and its tab children
 * -- live outside that family, and stripping a leading `modules.` here made the
 * portal write them to `feature_flags.modules.utilities.settings.*`, which
 * nothing reads.
 *
 * This is the property the whole schema-driven flag design leans on: a key
 * present in a tenant's stored JSON that the current manifest (or the portal's
 * own code) knows nothing about must survive a write, never be pruned. A
 * stored boolean switch survives too, as `{enabled: <it>}`. See
 * FlagTree.tsx's `readStored`/`decidingPath` for the read-side half of the
 * same contract.
 */
export function setPath(
  flags: Record<string, any>,
  path: string,
  value: any
): Record<string, any> {
  const parts = path.split('.');
  const next = JSON.parse(JSON.stringify(flags));
  let node: any = next;
  for (const part of parts.slice(0, -1)) {
    // A boolean node is a switch's shorthand (`tab: true` means `tab: {enabled: true}`):
    // keep the switch when writing beside it. Replacing it with {} would read as
    // the default, which for a tab that is off by default switches it off.
    if (typeof node[part] === 'boolean') node[part] = { enabled: node[part] };
    else if (typeof node[part] !== 'object' || node[part] === null) node[part] = {};
    node = node[part];
  }
  node[parts[parts.length - 1]] = value;
  return next;
}
